import { useState, useEffect, useCallback } from "react";
import { SPECIALIST_HOSTNAME } from "env-vars";
import { fetchWithAuth } from "helpers/api-with-auth";

/**
 * @deprecated Use Ehr from use-ehrs.ts instead.
 * EhrVendor is kept for backward compatibility - it now represents an Ehr (healthcare organization).
 */
export interface EhrVendor {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  // New fields from Ehr
  location_name?: string | null;
  city?: string | null;
  state?: string | null;
  vendor_name?: string | null;
}

interface UseEhrVendorsResult {
  vendors: EhrVendor[];
  isLoading: boolean;
  error: string | null;
  search: (query: string) => void;
}

/**
 * @deprecated Use useEhrs from use-ehrs.ts instead.
 * This hook now fetches EHRs (healthcare organizations) instead of vendors.
 */
export function useEhrVendors(): UseEhrVendorsResult {
  const [vendors, setVendors] = useState<EhrVendor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVendors = useCallback(async (query?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (query) {
        params.set("query", query);
      }

      // Now fetches from /ehrs endpoint instead of /ehr_vendors
      const url = `${SPECIALIST_HOSTNAME}/api/v1/ehrs?${params.toString()}`;
      const response = await fetchWithAuth(url);

      if (!response.ok) {
        throw new Error("Failed to fetch EHRs");
      }

      const data = await response.json();
      // Map to EhrVendor interface for backward compatibility
      const ehrs = data.ehrs || data;
      setVendors(ehrs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      setVendors([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const search = useCallback(
    (query: string) => {
      fetchVendors(query);
    },
    [fetchVendors]
  );

  return {
    vendors,
    isLoading,
    error,
    search,
  };
}
